import { useState, useCallback } from 'react';
import api from '../../api';
import { processImageFile, validateImageFile } from '../../utils/imageUtils';

/**
 * Custom hook for handling image uploads
 * Provides image validation, processing, and upload functionality
 */
export const useImageUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const upload = useCallback(async (file) => {
    if (!file) {
      setError('No file selected');
      return null;
    }

    setUploading(true);
    setError(null);

    try {
      // Validate the image file
      validateImageFile(file);

      // Process the image file (resize, compress)
      const processedFile = await processImageFile(file);

      // Create FormData and upload
      const formData = new FormData();
      formData.append('image', processedFile);

      const response = await api.post('/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data?.success && response.data.url) {
        return response.data.url;
      } else {
        throw new Error(response.data?.error || 'Failed to upload image');
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to upload image';
      setError(errorMsg);
      console.error('Error uploading image:', err);
      return null;
    } finally {
      setUploading(false);
    }
  }, []);

  return {
    upload,
    uploading,
    error,
    clearError: () => setError(null)
  };
};
